import { Vec3 } from './vec3';
import { AABB } from './aabb';

export class Ray {
  constructor(public origin = new Vec3(), public dir = new Vec3(0, 0, -1)) {}
  at(t: number, out = new Vec3()): Vec3 { return out.copy(this.origin).addScaled(this.dir, t); }

  /** Returns t of first hit with the box, or -1. Slab method. */
  intersectAABB(b: AABB): number {
    let tmin = -Infinity, tmax = Infinity;
    const o = this.origin, d = this.dir;
    const axes: ('x' | 'y' | 'z')[] = ['x', 'y', 'z'];
    for (const a of axes) {
      const inv = 1 / d[a];
      let t1 = (b.min[a] - o[a]) * inv, t2 = (b.max[a] - o[a]) * inv;
      if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
      tmin = Math.max(tmin, t1); tmax = Math.min(tmax, t2);
      if (tmax < tmin) return -1;
    }
    return tmax < 0 ? -1 : Math.max(tmin, 0);
  }

  /** Returns t of first hit with sphere, or -1. */
  intersectSphere(c: Vec3, r: number): number {
    const ox = this.origin.x - c.x, oy = this.origin.y - c.y, oz = this.origin.z - c.z;
    const b = ox * this.dir.x + oy * this.dir.y + oz * this.dir.z;
    const cc = ox * ox + oy * oy + oz * oz - r * r;
    const disc = b * b - cc;
    if (disc < 0) return -1;
    const s = Math.sqrt(disc);
    const t0 = -b - s, t1 = -b + s;
    if (t0 >= 0) return t0;
    if (t1 >= 0) return t1;
    return -1;
  }

  /** Intersect with vertical cylinder (axis Y) of radius r at (cx, cz), limited to [yMin, yMax]. Returns t or -1. */
  intersectVerticalCylinder(cx: number, cz: number, r: number, yMin: number, yMax: number): number {
    const ox = this.origin.x - cx, oz = this.origin.z - cz;
    const dx = this.dir.x, dz = this.dir.z;
    const a = dx * dx + dz * dz;
    if (a < 1e-12) return -1;
    const b = ox * dx + oz * dz;
    const c = ox * ox + oz * oz - r * r;
    const disc = b * b - a * c;
    if (disc < 0) return -1;
    const s = Math.sqrt(disc);
    const t0 = (-b - s) / a, t1 = (-b + s) / a;
    for (const t of [t0, t1]) {
      if (t < 0) continue;
      const y = this.origin.y + this.dir.y * t;
      if (y >= yMin && y <= yMax) return t;
    }
    return -1;
  }
}
